"use client";

import { Button } from "@/components/rac/button";
import { Tooltip, TooltipTrigger } from "@/components/rac/tooltip";
import { PropertyFiltersPopover } from "@/components/search/filters";
import { useCurrentCityName } from "@/hooks/use-current-city";
import { useUrlState } from "@/hooks/use-url-state";
import { useSearchPageMapData } from "@/providers/search";
import type { Filters } from "@/types/filters";
import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";
import { copyToClipboard, createShareableSearchUrl } from "@/utils/share-search";
import {
	Building2,
	Check,
	Eye,
	Home,
	List,
	Map as MapIcon,
	Maximize,
	Minimize,
	Satellite,
	Search,
	Share,
	X,
} from "lucide-react";
import { useCallback, useState } from "react";

interface UnifiedMapControlsProps {
	/** Current view (map or list) */
	view?: "map" | "list";
	/** Callback when view changes */
	onViewChange?: (view: "map" | "list") => void;
	/** Whether to show list/map toggle buttons. Default true */
	showViewToggles?: boolean;
	/** Whether to show share button. Default true */
	showShareButton?: boolean;
	/** Filters for the filter popover and share functionality */
	filters: Partial<Filters>;
	/** Custom class name for positioning */
	className?: string;
	/** Whether this is mobile view (affects styling) */
	isMobile?: boolean;
}

export function UnifiedMapControls({
	view = "map",
	onViewChange,
	showViewToggles = true,
	showShareButton = true,
	filters,
	className,
	isMobile = false,
}: UnifiedMapControlsProps) {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const [isSharing, setIsSharing] = useState(false);

	// Global map controls from store
	const mode = useSearchPageMapData(state => state.mode);
	const mapTypeId = useSearchPageMapData(state => state.mapTypeId);
	const setMapTypeId = useSearchPageMapData(state => state.setMapTypeId);
	const isFullScreen = useSearchPageMapData(state => state.isFullScreen);
	const setIsFullScreen = useSearchPageMapData(state => state.setIsFullScreen);

	// URL sync hook
	const { setMode: setUrlMode } = useUrlState();

	// Share functionality
	const city = useCurrentCityName();

	const handleModeChange = useCallback(
		(newMode: "default" | "project") => {
			setUrlMode(newMode);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[setUrlMode, mode],
	);

	const handleShare = async () => {
		setIsSharing(true);
		try {
			const { url } = createShareableSearchUrl(filters, city);

			// Use capability-based detection instead of userAgent regex
			const shareData = {
				title: `Property Search ${city ? `in ${city}` : ""}`,
				text: "Check out these properties I found",
				url,
			};

			const canNativeShare =
				typeof navigator !== "undefined" &&
				"share" in navigator &&
				(!("canShare" in navigator) ||
					(navigator as Navigator & { canShare: (data: ShareData) => boolean }).canShare(shareData));

			if (canNativeShare) {
				try {
					await navigator.share(shareData);
					return;
				} catch (shareErr) {
					// If user cancels share, don't fall back to clipboard
					if (shareErr instanceof DOMException && shareErr.name === "AbortError") {
						return;
					}
					logger.debug("Failed to share:", shareErr);
				}
			}

			// Fallback to clipboard copy
			const success = await copyToClipboard(url);
			if (success) {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			}
		} catch (error) {
			logger.error("Failed to share:", error);
		} finally {
			setIsSharing(false);
		}
	};

	// Common button styling matching the established design
	const fabButtonBase = cn(
		"h-10 w-10 p-0 rounded-full flex items-center justify-center",
		"!bg-black !text-white hover:!bg-black/90",
		"shadow-lg border border-white/20 transition-all duration-300",
	);

	const menuItemBase = cn(
		"h-10 px-4 rounded-xl text-white/70 hover:text-white hover:bg-white/20",
		"gap-3 transition-all w-full justify-start",
	);

	const menuItemActive = "bg-white text-black hover:bg-white hover:text-black shadow-sm";

	return (
		<>
			{/* Backdrop to close menu when clicking outside */}
			{isMenuOpen && <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />}

			{/* Container for both controls - they are independently positioned inside */}
			<div className={cn("z-50", className)}>
				{/* View Menu FAB - Positioned above the filter button */}
				<div className="absolute bottom-16 right-0">
					<div
						className={cn(
							"bg-black shadow-xl flex flex-col items-center overflow-hidden relative",
							isMenuOpen ? "h-auto w-auto min-w-[140px] p-2 rounded-3xl" : "h-10 w-10 rounded-full",
						)}
					>
						{/* Toggle Trigger - Visible ONLY when CLOSED */}
						{!isMenuOpen && (
							<TooltipTrigger>
								<Button
									variant="ghost"
									className={fabButtonBase}
									onPress={() => setIsMenuOpen(true)}
									aria-label="Open View Menu"
								>
									<Eye size={20} aria-hidden="true" />
								</Button>
								<Tooltip>View options</Tooltip>
							</TooltipTrigger>
						)}

						{/* Close Button - Visible ONLY when OPEN */}
						{isMenuOpen && (
							<div className="w-full flex justify-end pb-1">
								<TooltipTrigger>
									<Button
										variant="ghost"
										className="h-8 w-8 p-0 rounded-full text-white/70 hover:text-white hover:bg-white/10"
										onPress={() => setIsMenuOpen(false)}
										aria-label="Close Menu"
									>
										<X size={18} aria-hidden="true" />
									</Button>
									<Tooltip>Close menu</Tooltip>
								</TooltipTrigger>
							</div>
						)}

						{/* Expanded Menu Options */}
						<div
							className={cn(
								"flex flex-col-reverse items-center gap-1 overflow-hidden transition-all duration-300 w-full",
								isMenuOpen ? "h-auto opacity-100" : "h-0 opacity-0",
							)}
						>
							{/* OPTIONS FOR DESKTOP (Replacing Map/List view toggles) */}
							{!isMobile && (
								<>
									{/* Properties Mode */}
									<Button
										variant="ghost"
										className={cn(menuItemBase, mode === "default" && menuItemActive)}
										onPress={() => {
											handleModeChange("default");
											setIsMenuOpen(false);
										}}
										aria-label="Properties View"
									>
										<Home size={18} strokeWidth={2.5} aria-hidden="true" />
										<span className="text-sm font-medium whitespace-nowrap">Properties</span>
									</Button>

									{/* Projects Mode */}
									<Button
										variant="ghost"
										className={cn(menuItemBase, mode === "project" && menuItemActive)}
										onPress={() => {
											handleModeChange("project");
											setIsMenuOpen(false);
										}}
										aria-label="Projects View"
									>
										<Building2 size={18} strokeWidth={2.5} aria-hidden="true" />
										<span className="text-sm font-medium whitespace-nowrap">Projects</span>
									</Button>
								</>
							)}

							{/* OPTIONS FOR MOBILE (View Toggles: Map vs List) */}
							{isMobile && showViewToggles && (
								<>
									{/* Map View (also resets to Properties mode) */}
									<Button
										variant="ghost"
										className={cn(menuItemBase, view === "map" && mode === "default" && menuItemActive)}
										onPress={() => {
											handleModeChange("default");
											onViewChange?.("map");
											setIsMenuOpen(false);
										}}
										aria-label="Map View"
									>
										<MapIcon size={18} strokeWidth={2.5} aria-hidden="true" />
										<span className="text-sm font-medium whitespace-nowrap">Map</span>
									</Button>

									{/* List View */}
									<Button
										variant="ghost"
										className={cn(menuItemBase, view === "list" && menuItemActive)}
										onPress={() => {
											onViewChange?.("list");
											setIsMenuOpen(false);
										}}
										aria-label="List View"
									>
										<List size={18} strokeWidth={2.5} aria-hidden="true" />
										<span className="text-sm font-medium whitespace-nowrap">List</span>
									</Button>
								</>
							)}

							{/* SHARED OPTIONS (Satellite, Fullscreen) */}
							{/* Projects Mode (Mobile Only - Desktop has it above) */}
							{isMobile && (view === "map" || !showViewToggles) && (
								<Button
									variant="ghost"
									className={cn(menuItemBase, mode === "project" && menuItemActive)}
									onPress={() => {
										handleModeChange("project");
										onViewChange?.("map");
										setIsMenuOpen(false);
									}}
									aria-label="Project View"
								>
									<Building2 size={18} strokeWidth={2.5} aria-hidden="true" />
									<span className="text-sm font-medium whitespace-nowrap">Projects</span>
								</Button>
							)}

							{(view === "map" || !showViewToggles) && (
								<>
									{/* Satellite Toggle */}
									<Button
										variant="ghost"
										className={cn(menuItemBase, mapTypeId === "satellite" && menuItemActive)}
										onPress={() => {
											setMapTypeId(mapTypeId === "satellite" ? "roadmap" : "satellite");
											setIsMenuOpen(false);
										}}
										aria-label="Satellite View"
									>
										<Satellite size={18} strokeWidth={2.5} aria-hidden="true" />
										<span className="text-sm font-medium whitespace-nowrap">Satellite</span>
									</Button>

									{/* Fullscreen Toggle */}
									<Button
										variant="ghost"
										className={cn(menuItemBase, isFullScreen && menuItemActive)}
										onPress={() => {
											setIsFullScreen(!isFullScreen);
											setIsMenuOpen(false);
										}}
										aria-label={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
									>
										{isFullScreen ? (
											<Minimize size={18} strokeWidth={2.5} aria-hidden="true" />
										) : (
											<Maximize size={18} strokeWidth={2.5} aria-hidden="true" />
										)}
										<span className="text-sm font-medium whitespace-nowrap">
											{isFullScreen ? "Exit" : "Full Screen"}
										</span>
									</Button>
								</>
							)}

							{/* Share Button (in menu) */}
							{showShareButton && (
								<Button
									variant="ghost"
									className={cn(menuItemBase, copied && menuItemActive)}
									onPress={handleShare}
									isDisabled={isSharing}
									aria-label="Share search"
								>
									{copied ? (
										<Check size={18} strokeWidth={2.5} className="text-green-400" aria-hidden="true" />
									) : (
										<Share size={18} strokeWidth={2.5} aria-hidden="true" />
									)}
									<span className="text-sm font-medium whitespace-nowrap">{copied ? "Copied!" : "Share"}</span>
								</Button>
							)}
						</div>
					</div>
				</div>

				{/* Filter Button - Fixed at the bottom */}
				<div className="absolute bottom-0 right-0">
					<PropertyFiltersPopover
						isOpen={isFiltersOpen}
						onOpenChange={setIsFiltersOpen}
						defaultValues={filters}
						tooltip="Search & Filters"
						trigger={
							<Button
								variant="ghost"
								onPress={() => setIsFiltersOpen(true)}
								className={cn(
									"h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg border border-white/20",
									"!bg-black !text-white hover:!bg-black/90",
									isFiltersOpen ? "px-4 gap-2" : "px-0 w-10",
								)}
								aria-label="Filters"
							>
								<span className="flex-shrink-0">
									<Search size={20} strokeWidth={2.5} aria-hidden="true" />
								</span>
								<span
									className={cn(
										"text-sm font-medium whitespace-nowrap transition-all duration-300 overflow-hidden",
										isFiltersOpen ? "max-w-24 opacity-100" : "max-w-0 opacity-0",
									)}
								>
									Filters
								</span>
							</Button>
						}
					/>
				</div>
			</div>
		</>
	);
}
